import dagre from "dagre";
import { isEmpty } from "lodash";
import { Edge, Node, Position } from "reactflow";

export enum Direction {
    Vertical = "TB",
    Horizontal = "LR",
}

// Arranges nodes in a grid (sqrt(N) columns) when dagre would collapse them into one rank.
// rowSpacing is the vertical gap between rows; colSpacing the horizontal gap between
// columns (defaults to rowSpacing, so callers that don't distinguish the two axes keep
// square spacing). The object/template canvases pass a smaller colSpacing to tighten the
// horizontal gap without changing vertical spacing.
const gridLayout = (
    nodes: Node[],
    offsetLeft: number,
    offsetTop: number,
    direction: Direction,
    rowSpacing: number,
    colSpacing: number = rowSpacing
): Node[] => {
    const isVertical = direction !== Direction.Horizontal;
    const cols = Math.ceil(Math.sqrt(nodes.length));
    const rowCount = Math.ceil(nodes.length / cols);
    const rowMaxHeights = new Array(rowCount).fill(0);
    const colMaxWidths = new Array(cols).fill(0);

    nodes.forEach((node, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        rowMaxHeights[row] = Math.max(rowMaxHeights[row], node.height ?? 0);
        colMaxWidths[col] = Math.max(colMaxWidths[col], node.width ?? 0);
    });

    const rowOffsets: number[] = [offsetTop];
    for (let r = 0; r < rowCount - 1; r++) {
        rowOffsets.push(rowOffsets[r] + rowMaxHeights[r] + rowSpacing);
    }
    const colOffsets: number[] = [offsetLeft];
    for (let c = 0; c < cols - 1; c++) {
        colOffsets.push(colOffsets[c] + colMaxWidths[c] + colSpacing);
    }

    return nodes.map((node: any, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        return {
            ...node,
            targetPosition: isVertical ? Position.Top : Position.Left,
            sourcePosition: isVertical ? Position.Bottom : Position.Right,
            position: {
                x: colOffsets[col],
                y: rowOffsets[row],
            },
        };
    });
};

export const getPositionedGraph = (
    nodes: Node[],
    edges: Edge[],
    offsetLeft: number,
    offsetTop: number,
    direction = Direction.Vertical,
    spacing: number = 50,
    // When true, fall back to a grid layout if edges don't connect any nodes in the list.
    // Callers that intentionally want a single row of disconnected nodes (e.g. positionChildrenNodes)
    // should keep the default false.
    useGridFallback = false,
    // Optional independent horizontal spacing (dagre nodesep + grid column gap).
    // Defaults to `spacing`, so callers that don't distinguish the two axes keep
    // square spacing. `spacing` continues to drive the vertical gap (ranksep / row gap).
    nodeSpacing?: number
): [Node[], Edge[]] => {
    if (isEmpty(nodes)) return [[], []];

    const horizontalSpacing = nodeSpacing ?? spacing;

    if (useGridFallback && nodes.length > 1) {
        const nodeIds = new Set(nodes.map((n) => n.id));
        const validEdges = edges?.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target)) ?? [];
        if (validEdges.length === 0) {
            return [
                gridLayout(nodes, offsetLeft, offsetTop, direction, spacing, horizontalSpacing),
                edges,
            ];
        }
    }

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
        rankdir: direction,
        nodesep: horizontalSpacing,
        edgesep: horizontalSpacing,
        ranksep: spacing,
    });

    nodes.forEach((node: Node) => {
        // TODO replace this with typecheck, requires refactoring
        if (node.width == null || node.width <= 0 || node.height == null || node.height <= 0) {
            console.error(
                "node width and height must be specified and greater than zero for positioning"
            );
        }
        dagreGraph.setNode(node.id, { width: node.width, height: node.height });
    });
    edges?.forEach((edge: Edge) => dagreGraph.setEdge(edge.source, edge.target));

    // TODO (medium) improve error handling
    try {
        dagre.layout(dagreGraph);
    } catch (e) {
        console.error(e);
    }

    const positionedNodes = nodes.map((node: any) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        node.targetPosition = direction === Direction.Horizontal ? Position.Left : Position.Top;
        node.sourcePosition = direction === Direction.Horizontal ? Position.Right : Position.Bottom;

        // shift dagre node position (center center) so it matches the react flow anchor point (top left)
        const positionendNode = {
            ...node,
            position: {
                x: nodeWithPosition.x - node.width / 2 + offsetLeft,
                y: nodeWithPosition.y - node.height / 2 + offsetTop,
            },
        };

        return positionendNode;
    });

    return [positionedNodes, edges];
};

export const getSize = (nodes: Node[]): [number, number] => {
    const xLefts = nodes.map((node) => node.position.x);
    const yTops = nodes.map((node) => node.position.y);
    const xRights = nodes.map((node) => node.position.x + (node.width ?? 0));
    const yBottoms = nodes.map((node) => node.position.y + (node.height ?? 0));

    const minX = Math.min(...xLefts);
    const maxX = Math.max(...xRights);
    const minY = Math.min(...yTops);
    const maxY = Math.max(...yBottoms);

    const width = maxX - minX;
    const height = maxY - minY;
    return [width, height];
};
